// components/admin/MusicManager.jsx
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Edit2, Upload, AlertCircle, Grid3x3, List, Search, X, Disc3, FolderOpen, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { ref as dbRef, set, remove, push } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'; // ADDED: deleteObject

const storage = getStorage();

const MUSIC_TYPES = [
  { value: 'song', label: 'Song' },
  { value: 'instrumental', label: 'Instrumental' },
  { value: 'piano', label: 'Piano Track' },
  { value: 'minus-one', label: 'Minus 1' },
  { value: 'karaoke', label: 'Karaoke' },
  { value: 'acoustic', label: 'Acoustic' },
  { value: 'cover', label: 'Cover' },
  { value: 'remix', label: 'Remix' },
  { value: 'other', label: 'Other' }
];

const formatTime = (time) => {
  if (!time || isNaN(time) || time < 0) return '0:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const MusicPreviewPlayer = ({ src }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audio.currentTime = percent * duration;
    setCurrentTime(audio.currentTime);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !audio.muted;
    setIsMuted(audio.muted);
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex items-center gap-2 bg-[#1e2a2f] rounded-lg border border-[#bd9f67]/15 px-2.5 py-2">
      <button
        onClick={togglePlay}
        className="flex items-center justify-center w-7 h-7 rounded-full bg-[#bd9f67] hover:bg-[#d0b07a] text-[#243137] transition flex-shrink-0"
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? <Pause size={13} fill="currentColor" /> : <Play size={13} fill="currentColor" className="ml-0.5" />}
      </button>

      <span className="text-[10px] text-[#a6a6b5] w-9 text-right tabular-nums flex-shrink-0">
        {formatTime(currentTime)}
      </span>

      <div
        onClick={handleSeek}
        className="relative flex-1 h-1.5 bg-white/10 rounded-full cursor-pointer group"
        role="slider"
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={duration || 0}
        aria-valuenow={currentTime}
      >
        <div
          className="absolute inset-y-0 left-0 bg-[#bd9f67] rounded-full group-hover:bg-[#d0b07a] transition-colors"
          style={{ width: `${progress}%` }}
        />
      </div>

      <span className="text-[10px] text-[#a6a6b5] w-9 tabular-nums flex-shrink-0">
        {formatTime(duration)}
      </span>

      <button
        onClick={toggleMute}
        className="text-[#a6a6b5] hover:text-[#bd9f67] transition flex-shrink-0"
        aria-label={isMuted ? 'Unmute' : 'Mute'}
      >
        {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
      </button>

      <audio
        ref={audioRef}
        src={src}
        preload="none"
        onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onEnded={() => setIsPlaying(false)}
      />
    </div>
  );
};

export default function MusicManager({ db, showToast, music }) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ 
    name: '', 
    artist: '', 
    url: '', 
    type: 'song',
    album: '',
    trackNumber: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  // NEW STATE: To track the file in storage for cleanup
  const [uploadedFilePath, setUploadedFilePath] = useState(null); 

  const [viewMode, setViewMode] = useState('card');
  const [lastAddedId, setLastAddedId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedAlbum, setSelectedAlbum] = useState('all');
  const [groupByAlbum, setGroupByAlbum] = useState(false);
  const fileInputRef = useRef(null);
  const lastItemRef = useRef(null);

  const generateShortFilename = (originalName) => {
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 8);
    const extension = originalName.split('.').pop();
    return `${timestamp}-${randomId}.${extension}`;
  };

  // Get unique albums from music
  const albums = [...new Set(music.map(track => track.album).filter(Boolean))].sort();

  // Filter music based on search, type, and album
  const filteredMusic = music.filter((track) => {
    const matchesSearch = 
      track.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (track.artist || 'The Asidors').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (track.album || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === 'all' || track.type === selectedType;
    const matchesAlbum = selectedAlbum === 'all' || track.album === selectedAlbum;
    return matchesSearch && matchesType && matchesAlbum;
  });

  // Group music by album
  const groupedByAlbum = filteredMusic.reduce((acc, track) => {
    const albumName = track.album || 'No Album';
    if (!acc[albumName]) {
      acc[albumName] = [];
    }
    acc[albumName].push(track);
    return acc;
  }, {});

  // Sort tracks within each album by track number
  Object.keys(groupedByAlbum).forEach(albumName => {
    groupedByAlbum[albumName].sort((a, b) => {
      const trackA = parseInt(a.trackNumber) || 999;
      const trackB = parseInt(b.trackNumber) || 999;
      return trackA - trackB;
    });
  });

  // Auto-scroll to newly added item
  useEffect(() => {
    if (lastAddedId !== null && lastItemRef.current) {
      setTimeout(() => {
        lastItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [lastAddedId, filteredMusic.length]);

  // NEW FUNCTION: Cleanup the file from Storage
  const cleanupUpload = async (filePath) => {
    if (!filePath) return;

    try {
        const fileRef = storageRef(storage, filePath);
        await deleteObject(fileRef);
        console.log(`Cleaned up file from Storage: ${filePath}`);
    } catch (error) {
        // Log the error but don't show a toast to the user for a background task
        console.error("Error cleaning up storage file:", error.message);
    }
    setUploadedFilePath(null);
    setFileUploaded(false);
    setFormData(prev => ({ ...prev, url: '' }));
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type - only mp3 and wav
    const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/wave', 'audio/x-wav'];
    const allowedExtensions = ['.mp3', '.wav'];
    
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      const errorMsg = 'Only MP3 and WAV files are allowed';
      showToast(errorMsg, 'error');
      setUploadError(errorMsg);
      return;
    }

    // Validate file size (100MB max for audio files)
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      const errorMsg = 'File size must be less than 100MB';
      showToast(errorMsg, 'error');
      setUploadError(errorMsg);
      return;
    }

    // If an old file was successfully uploaded but not saved, clean it up first
    if (uploadedFilePath) {
        await cleanupUpload(uploadedFilePath);
    }
    
    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      const shortFilename = generateShortFilename(file.name);
      // Store the full path for later cleanup
      const fullPath = `music/${shortFilename}`; 
      const storageReference = storageRef(storage, fullPath);
      
      const uploadTask = uploadBytes(storageReference, file);
      
      uploadTask.then(() => {
        setUploadProgress(100);
      }).catch((err) => {
        console.error('Upload error:', err);
        const errorMsg = err.code === 'storage/unauthorized' 
          ? 'Permission denied. Please check your authentication.' 
          : 'Error uploading file';
        showToast(errorMsg, 'error');
        setUploadError(errorMsg);
        setUploading(false);
        setUploadProgress(0);
        setFileUploaded(false);
      });

      await uploadTask;
      const downloadUrl = await getDownloadURL(storageReference);
      setFormData({ ...formData, url: downloadUrl });
      setFileUploaded(true);
      setUploadError(null);
      // Store the path for potential cleanup later
      setUploadedFilePath(fullPath); 
      showToast('Music file uploaded successfully!', 'success');
      setUploadProgress(0);
    } catch (err) {
      console.error('Upload error:', err);
      const errorMsg = err.code === 'storage/unauthorized' 
        ? 'Permission denied. Please check your authentication.' 
        : 'Error uploading file';
      showToast(errorMsg, 'error');
      setUploadError(errorMsg);
      setUploadProgress(0);
      setFileUploaded(false);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // --- REQUIRED FIELD VALIDATION ---
    if (!formData.name) {
      showToast('Please enter the Song Name', 'error');
      return;
    }
    if (!formData.artist) {
      showToast('Please enter the Artist Name', 'error');
      return;
    }
    if (!formData.album) {
      showToast('Please enter the Album Name', 'error');
      return;
    }
    if (!formData.trackNumber || parseInt(formData.trackNumber) <= 0) {
      showToast('Please enter a valid Track Number (must be 1 or greater)', 'error');
      return;
    }
    if (!formData.type) {
        showToast('Please select a Music Type', 'error');
        return;
    }

    if (!fileUploaded || !formData.url) {
      showToast('Music file must be uploaded before saving', 'error');
      return;
    }
    // ---------------------------------

    let savedSuccessfully = false; // Flag to track database save status

    try {
      const musicData = {
        name: formData.name,
        artist: formData.artist,
        url: formData.url,
        type: formData.type,
        album: formData.album,
        trackNumber: parseInt(formData.trackNumber)
      };

      if (editingId) {
        await set(dbRef(db, `content/music/${editingId}`), {
          id: editingId,
          ...musicData
        });
        showToast('Music updated successfully!');
        savedSuccessfully = true;
      } else {
        const newMusicRef = push(dbRef(db, 'content/music'));
        const id = newMusicRef.key;
        await set(newMusicRef, {
          id,
          ...musicData
        });
        setLastAddedId(id);
        setShowPreview(true);
        showToast('🎵 Music added successfully!', 'success');
        savedSuccessfully = true;
      }
    } catch (err) {
      // If saving to DB fails, the file must be cleaned up!
      console.error("Database save failed:", err);
      showToast('Error saving music to database. Deleting file from storage...', 'error');
      // The cleanup is called in the finally block below
    } finally {
        if (savedSuccessfully) {
            // If saved, clear the file path tracker
            setUploadedFilePath(null); 
        } else if (uploadedFilePath) {
            // If failed, clean up the file
            await cleanupUpload(uploadedFilePath);
        }
        
        // Reset form state regardless of success/failure
        setFormData({ name: '', artist: '', url: '', type: 'song', album: '', trackNumber: '' });
        setEditingId(null);
        setShowForm(false);
        setFileUploaded(false);
        setUploadError(null);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await remove(dbRef(db, `content/music/${id}`));
        showToast('Music deleted successfully!');
      } catch (err) {
        showToast('Error deleting music', 'error');
      }
    }
  };

  const handleEdit = (track, id) => {
    // If we start editing, clear any pending upload state from a previous session
    if (uploadedFilePath) {
        cleanupUpload(uploadedFilePath);
    }
    setFormData({ 
      name: track.name, 
      artist: track.artist || '', 
      url: track.url,
      type: track.type || 'song',
      album: track.album || '',
      trackNumber: track.trackNumber ? String(track.trackNumber) : ''
    });
    setEditingId(id);
    setShowForm(true);
    setFileUploaded(true);
    setUploadError(null);
  };

  const handleRetryUpload = () => {
    // Retry means we should still delete any pending file and start clean
    if (uploadedFilePath) {
        cleanupUpload(uploadedFilePath);
    }
    setFileUploaded(false);
    setUploadError(null);
    setFormData({ ...formData, url: '' });
    fileInputRef.current?.click();
  };

  const getTypeLabel = (type) => {
    return MUSIC_TYPES.find(t => t.value === type)?.label || type;
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      'song': 'bg-blue-500/15 text-blue-200',
      'instrumental': 'bg-green-500/15 text-green-200',
      'piano': 'bg-purple-500/15 text-purple-200',
      'minus-one': 'bg-orange-500/15 text-orange-200',
      'karaoke': 'bg-pink-500/15 text-pink-200',
      'acoustic': 'bg-amber-500/15 text-amber-200',
      'cover': 'bg-indigo-500/15 text-indigo-200',
      'remix': 'bg-red-500/15 text-red-200',
      'other': 'bg-[#1e1e25] text-[#f9f9f9]'
    };
    return colors[type] || 'bg-[#1e1e25] text-[#f9f9f9]';
  };

  const renderTrackCard = (track) => (
    <motion.div
      key={track.id}
      ref={track.id === lastAddedId ? lastItemRef : null}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`group relative bg-[#243137] rounded-xl overflow-hidden p-5 transition-all duration-500 hover:scale-[1.04] hover:rounded-none hover:shadow-[0_0_25px_rgba(189,159,103,0.18)] ${
        track.id === lastAddedId ? 'ring-2 ring-[#bd9f67]' : ''
      }`}
    >
      {/* Animated gold border */}
      <div className="pointer-events-none absolute inset-0 border-2 border-[#bd9f67] opacity-0 rotate-[10deg] transition-all duration-500 group-hover:inset-3 group-hover:opacity-100 group-hover:rotate-0"></div>

      {/* Decorative accents */}
      <div className="pointer-events-none absolute top-4 right-5 w-2 h-2 rounded-full bg-[#bd9f67]/40 transition-colors duration-500 group-hover:bg-[#bd9f67]/70"></div>
      <div className="pointer-events-none absolute top-4 right-9 h-4 w-px bg-[#bd9f67]/40"></div>

      {track.id === lastAddedId && (
        <div className="absolute top-2 left-2 z-10 px-2 py-0.5 bg-[#bd9f67] text-[#243137] text-[10px] font-bold rounded">
          ✨ New
        </div>
      )}

      <div className="relative flex flex-col min-h-[260px]">
        {/* Header badges */}
        <div className="flex items-start justify-between mb-2 gap-2">
          <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${getTypeBadgeColor(track.type || 'song')}`}>
            {getTypeLabel(track.type || 'song')}
          </span>
          {track.trackNumber && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold text-[#bd9f67] bg-[#bd9f67]/10 border border-[#bd9f67]/20">
              #{track.trackNumber}
            </span>
          )}
        </div>

        {/* Center content */}
        <div className="flex-1 flex flex-col items-center justify-center text-center py-3">
          <div className="w-12 h-12 rounded-full bg-[#bd9f67]/10 border border-[#bd9f67]/25 flex items-center justify-center mb-3 transition-all duration-500 group-hover:scale-110">
            <Disc3 size={22} className="text-[#bd9f67]" />
          </div>
          <h3 className="text-[#f9f9f9] font-semibold text-sm md:text-base mb-1 truncate w-full">{track.name}</h3>
          <p className="text-[#a6a6b5] text-xs mb-1 truncate w-full">{track.artist || 'The Asidors'}</p>
          {track.album && !groupByAlbum && (
            <p className="text-[#7d7d8a] text-[11px] truncate w-full">{track.album}</p>
          )}
        </div>

        {/* Audio */}
        <MusicPreviewPlayer src={track.url} />

        {/* Actions */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => handleEdit(track, track.id)}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-[#bd9f67]/20 text-[#f9f9f9] rounded-lg transition text-xs"
          >
            <Edit2 size={13} /> Edit
          </button>
          <button
            onClick={() => handleDelete(track.id)}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-red-500/20 text-red-300 rounded-lg transition text-xs"
          >
            <Trash2 size={13} /> Delete
          </button>
        </div>

        {/* Bottom signature label (design) */}
        <span className="pointer-events-none absolute left-1/2 bottom-1.5 -translate-x-1/2 text-[8px] uppercase tracking-[0.35em] text-[#bd9f67] bg-[#243137] px-2 py-0.5 opacity-0 transition-all duration-500 group-hover:opacity-100 group-hover:tracking-[0.18em] whitespace-nowrap">
          The Asidors
        </span>
      </div>
    </motion.div>
  );

  const renderTrackRow = (track) => (
    <motion.tr
      key={track.id}
      ref={track.id === lastAddedId ? lastItemRef : null}
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className={`border-b border-[#2a2a33] hover:bg-[#1e1e25] transition ${
        track.id === lastAddedId ? 'bg-green-50' : ''
      }`}
    >
      {!groupByAlbum && (
        <td className="px-6 py-4">
          <p className="text-sm text-[#a6a6b5] truncate">{track.album || '-'}</p>
        </td>
      )}
      <td className="px-6 py-4">
        <p className="text-sm text-[#7d7d8a]">{track.trackNumber || '-'}</p>
      </td>
      <td className="px-6 py-4">
        <p className="text-sm font-medium text-[#f9f9f9] truncate">{track.name}</p>
      </td>
      <td className="px-6 py-4">
        <p className="text-sm text-[#a6a6b5] truncate">{track.artist || 'The Asidors'}</p>
      </td>
      <td className="px-6 py-4">
        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getTypeBadgeColor(track.type || 'song')}`}>
          {getTypeLabel(track.type || 'song')}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex justify-center gap-2">
          <button
            onClick={() => handleEdit(track, track.id)}
            className="flex items-center gap-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition text-sm"
          >
            <Edit2 size={14} /> Edit
          </button>
          <button
            onClick={() => handleDelete(track.id)}
            className="flex items-center gap-1 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded transition text-sm"
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </td>
    </motion.tr>
  );

  const handleCancel = () => {
    // If there is a file uploaded but not yet saved, delete it from storage
    if (uploadedFilePath) {
      cleanupUpload(uploadedFilePath);
    }

    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', artist: '', url: '', type: 'song', album: '', trackNumber: '' });
    setUploadProgress(0);
    setFileUploaded(false);
    setUploadError(null);
  };

  return (
    <div>
      <div className="flex flex-col gap-6 mb-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              // Existing logic simplified to call handleCancel to ensure cleanup
              handleCancel(); 
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-6 py-3 bg-[#bd9f67] hover:bg-[#d0b07a] text-[#243137] rounded-lg font-medium transition shadow-lg shadow-[#bd9f67]/10"
          >
            <Plus size={20} /> Add Music
          </motion.button>

          {music.length > 0 && (
            <div className="flex gap-2 bg-[#243137] rounded-lg p-1 border border-[#bd9f67]/20">
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-2 px-4 py-2 rounded transition ${
                  viewMode === 'table'
                    ? 'bg-[#bd9f67] text-[#243137] shadow'
                    : 'text-[#a6a6b5] hover:text-[#bd9f67]'
                }`}
              >
                <List size={18} /> Table
              </button>
              <button
                onClick={() => setViewMode('card')}
                className={`flex items-center gap-2 px-4 py-2 rounded transition ${
                  viewMode === 'card'
                    ? 'bg-[#bd9f67] text-[#243137] shadow'
                    : 'text-[#a6a6b5] hover:text-[#bd9f67]'
                }`}
              >
                <Grid3x3 size={18} /> Card
              </button>
            </div>
          )}
        </div>

        {/* Search and Filter */}
        {music.length > 0 && (
          <div className="flex flex-col gap-4 bg-[#16161b] p-4 rounded-lg shadow">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="flex-1 relative">
                <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#7d7d8a]" />
                <input
                  type="text"
                  placeholder="Search by song, artist, or album..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-transparent text-[#c9c9d4] border border-[#33333d] rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#7d7d8a] hover:text-[#a6a6b5]"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-4 py-2 border border-[#33333d] rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-transparent text-[#c9c9d4]"
              >
                <option value="all">All Types</option>
                {MUSIC_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <select
                value={selectedAlbum}
                onChange={(e) => setSelectedAlbum(e.target.value)}
                className="px-4 py-2 border border-[#33333d] rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-transparent text-[#c9c9d4]"
              >
                <option value="all">All Albums</option>
                <option value="">No Album</option>
                {albums.map((album) => (
                  <option key={album} value={album}>
                    {album}
                  </option>
                ))}
              </select>
            </div>
            
            {albums.length > 0 && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="groupByAlbum"
                  checked={groupByAlbum}
                  onChange={(e) => setGroupByAlbum(e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-[#33333d] rounded focus:ring-purple-500"
                />
                <label htmlFor="groupByAlbum" className="text-sm text-[#c9c9d4] cursor-pointer flex items-center gap-2">
                  <FolderOpen size={16} />
                  Group by Album
                </label>
              </div>
            )}
          </div>
        )}
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#16161b] rounded-lg shadow-lg p-6 mb-8"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[#c9c9d4] font-medium mb-2">Song Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Ikaw Ang Kusog"
                  className="w-full px-4 py-2 bg-transparent text-[#c9c9d4] border border-[#33333d] rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required 
                />
              </div>

              <div>
                <label className="block text-[#c9c9d4] font-medium mb-2">Artist *</label>
                <input
                  type="text"
                  value={formData.artist}
                  onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                  placeholder="e.g., The Asidors"
                  className="w-full px-4 py-2 bg-transparent text-[#c9c9d4] border border-[#33333d] rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required 
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-[#c9c9d4] font-medium mb-2">Music Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-2 border border-[#33333d] rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-transparent text-[#c9c9d4]"
                  required 
                >
                  {MUSIC_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[#c9c9d4] font-medium mb-2">Album Name *</label>
                <input
                  type="text"
                  value={formData.album}
                  onChange={(e) => setFormData({ ...formData, album: e.target.value })}
                  placeholder="e.g., Greatest Hits"
                  list="albumsList"
                  className="w-full px-4 py-2 bg-transparent text-[#c9c9d4] border border-[#33333d] rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required 
                />
                <datalist id="albumsList">
                  {albums.map((album) => (
                    <option key={album} value={album} />
                  ))}
                </datalist>
              </div>

              <div>
                <label className="block text-[#c9c9d4] font-medium mb-2">Track Number *</label>
                <input
                  type="number"
                  min="1"
                  value={formData.trackNumber}
                  onChange={(e) => setFormData({ ...formData, trackNumber: e.target.value })}
                  placeholder="e.g., 1"
                  className="w-full px-4 py-2 bg-transparent text-[#c9c9d4] border border-[#33333d] rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required 
                />
              </div>
            </div>

            <div>
              <label className="block text-[#c9c9d4] font-medium mb-2">Upload Music File *</label>
              <div className="flex gap-3">
                <input
                  type="file"
                  accept=".mp3,.wav,audio/mpeg,audio/wav"
                  ref={fileInputRef}
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                  className="hidden"
                  disabled={uploading}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition disabled:opacity-50"
                >
                  <Upload size={16} /> {uploading ? 'Uploading...' : 'Upload Music'}
                </button>
              </div>
              
              {uploading && (
                <div className="mt-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-[#c9c9d4]">Upload Progress</span>
                    <span className="text-sm font-semibold text-blue-600">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-[#26262d] rounded-full h-2.5">
                    <motion.div
                      className="bg-blue-500 h-2.5 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${uploadProgress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </div>
              )}
              
              {fileUploaded && !uploading && !uploadError && (
                <p className="text-sm text-green-600 mt-2">✓ Music file uploaded</p>
              )}

              {uploadError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex gap-2 items-center justify-between">
                  <div className="flex gap-2">
                    <AlertCircle size={18} className="text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-800">{uploadError}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleRetryUpload}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium transition"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!fileUploaded && !uploading && formData.name && !uploadError && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex gap-2">
                  <AlertCircle size={18} className="text-yellow-600 flex-shrink-0" />
                  <p className="text-sm text-yellow-800">Upload a music file before saving</p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={uploading || !fileUploaded}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingId ? 'Update Music' : 'Add Music'}
              </button>
              <button
                type="button"
                onClick={handleCancel} // UPDATED to use the cleanup function
                disabled={uploading}
                className="flex-1 px-4 py-2 bg-[#1e1e25] hover:bg-[#26262d] text-[#c9c9d4] rounded-lg font-medium transition disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Preview newly added item */}
      {showPreview && lastAddedId && filteredMusic.length > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="mb-8 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg"
        >
          <p className="text-green-800 font-medium">✓ Music added! Scrolling to show the new track...</p>
        </motion.div>
      )}

      {/* Results count */}
      {music.length > 0 && (
        <p className="text-sm text-[#a6a6b5] mb-4">
          Showing {filteredMusic.length} of {music.length} tracks
          {albums.length > 0 && ` • ${albums.length} album${albums.length !== 1 ? 's' : ''}`}
        </p>
      )}

      {/* Grouped by Album View */}
      {groupByAlbum && Object.keys(groupedByAlbum).length > 0 ? (
        <div className="space-y-8">
          {Object.entries(groupedByAlbum).sort(([a], [b]) => {
            if (a === 'No Album') return 1;
            if (b === 'No Album') return -1;
            return a.localeCompare(b);
          }).map(([albumName, tracks]) => (
            <div key={albumName} className="bg-[#16161b] rounded-lg shadow-lg overflow-hidden border border-[#2a2a33]">
              <div className="bg-gradient-to-r from-[#243137] to-[#2d3f45] p-6 text-white border-b border-[#bd9f67]/20">
                <div className="flex items-center gap-3">
                  <Disc3 size={32} className="text-[#bd9f67]" />
                  <div>
                    <h2 className="text-2xl font-bold">{albumName}</h2>
                    <p className="text-[#a6a6b5]">{tracks.length} track{tracks.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>
              
              {viewMode === 'card' ? (
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 p-6">
                  {tracks.map((track) => renderTrackCard(track))}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#1e1e25] border-b border-[#2a2a33]">
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#c9c9d4]">#</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#c9c9d4]">Song Name</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#c9c9d4]">Artist</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#c9c9d4]">Type</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold text-[#c9c9d4]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tracks.map((track) => renderTrackRow({ ...track, trackNumber: track.trackNumber || '-' }))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Table View */}
          {viewMode === 'table' && (
            <>
              {filteredMusic.length > 0 ? (
                <div className="bg-[#16161b] rounded-lg shadow-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#1e1e25] border-b border-[#2a2a33]">
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#c9c9d4]">Album</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#c9c9d4]">#</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#c9c9d4]">Song Name</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#c9c9d4]">Artist</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#c9c9d4]">Type</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold text-[#c9c9d4]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMusic.map((track) => renderTrackRow(track))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-[#7d7d8a] py-8">No tracks found matching your search</p>
              )}
            </>
          )}

          {/* Card View */}
          {viewMode === 'card' && (
            <>
              {filteredMusic.length > 0 ? (
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                  {filteredMusic.map((track) => renderTrackCard(track))}
                </div>
              ) : (
                <p className="text-center text-[#7d7d8a] py-8">No tracks found matching your search</p>
              )}
            </>
          )}
        </>
      )}

      {music.length === 0 && (
        <p className="text-center text-[#7d7d8a] py-8">No music yet. Add one to get started!</p>
      )}
    </div>
  );
}